import React, { useState, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    CardBody,
    FormGroup,
    Button,
    CardTitle,
    CardSubtitle,
    Label,
    Input,
    Container,
    Form,
    FormFeedback,
    UncontrolledAlert,
    Spinner // Import Spinner component for loading indicator
} from "reactstrap";
import { useParams, useNavigate } from 'react-router-dom';
import * as Yup from "yup";
import { useFormik } from "formik";
import { get, put, del } from "../../Services/RestAPIService";
import NoImage from "../../assets/images/No_Image_Available.jpg";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import AlertModal from "../../components/AlertModal"; // Import AlertModal component
import { withTranslation } from 'react-i18next'; // Import withTranslation

const FormValidations = ({ t }) => {
    const [categoryData, setCategoryData] = useState(null);
    const [categoryImage, setCategoryImage] = useState(null);
    const [alert, setAlert] = useState({
        color: "",
        message: "",
        visible: false,
        strongM: ""
    });
    const [isLoading, setIsLoading] = useState(false); // State to track loading status
    const [isLoadingsub, setIsLoadingsub] = useState(false); // State to track loading status

    const [isAlertModal, setIsAlertModal] = useState(false); // State to Display Modal as we Delete

    const { id } = useParams();
    const navigate = useNavigate();

    // Function to fetch category data
    const getCategoryData = async () => {
        try {
            const data = await get(`category/categories/${id}/`);
            setCategoryData(data);
            console.log(data)
        } catch (error) {
            console.error('Error fetching category data:', error);
        }
    };

    useEffect(() => {
        getCategoryData();
    }, []);

    // Form validation with Formik and Yup
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: categoryData ? categoryData.name : '',
            description: categoryData ? categoryData.description : '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required(t("Please Enter Category Name")),
            description: Yup.string().required(t("Please Enter Description")),
        }),
        onSubmit: async (values) => {
            setIsLoadingsub(true)
            const Data = {
                "name": values.name,
                "description": values.description,
                "slug": "",
                ...(categoryImage && { "image": categoryImage }) // Include image only if categoryImage is not null
            }

            try {
                const response = await put(`category/categories/${id}/`, Data, {
                    headers: { 'Content-Type': 'application/json' },
                    validateStatus: false
                });

                if (response.status !== 400) {
                    setAlert({
                        color: "success",
                        visible: true,
                        message: t("Category data updated successfully!"),
                        strongM: t("Success!")
                    });
                    setTimeout(() => {
                        setAlert({ visible: false });
                    }, 3000);
                } else {
                    const errorMessage = response.data?.error || t("An error occurred while updating category data.");
                    setAlert({
                        color: "danger",
                        visible: true,
                        message: errorMessage,
                        strongM: t("Error!")
                    });
                    setTimeout(() => {
                        setAlert({ visible: false });
                    }, 3000);
                }
            } catch (error) {
                console.error('Error updating category data:', error);
                setAlert({
                    color: "danger",
                    visible: true,
                    message: t("Error updating category data"),
                    strongM: t("Error!")
                });
                setTimeout(() => {
                    setAlert({ visible: false });
                }, 3000);
            }
            finally {
                setIsLoadingsub(false)

            }
        }
    });

    // Function to handle image change
    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                setCategoryImage(base64String);
            };
            reader.readAsDataURL(file);
        }
    };

    // Function to delete category
    const handleDelete = async () => {
        setIsLoading(true); // Set loading state to true when deleting
        setIsAlertModal(false); // Close Modal

        try {
            const response = await del(`category/categories/${id}/`);
            if (response.status <= 400) {
                setAlert({
                    color: "success",
                    visible: true,
                    message: t("Category deleted successfully!"),
                    strongM: t("Success!")
                });
                setTimeout(() => {
                    setAlert({ visible: false });
                    navigate("/category");
                }, 3000);
            } else {
                const errorMessage = response.data?.error || t("An error occurred while deleting category.");
                setAlert({
                    color: "danger",
                    visible: true,
                    message: errorMessage,
                    strongM: t("Error!")
                });
                setTimeout(() => {
                    setAlert({ visible: false });
                }, 3000);
            }
        } catch (error) {
            console.error('Error deleting category:', error);
            setAlert({
                color: "danger",
                visible: true,
                message: t("Error Deleting Category"),
                strongM: t("Error!")
            });
            setTimeout(() => {
                setAlert({ visible: false });
            }, 3000);
        } finally {
            setIsLoading(false); // Set loading state back to false after deletion
            setTimeout(() => {
                setAlert({ visible: false });
                navigate(`/category`);
            }, 1500);

        }
    };

    return (
        <React.Fragment>
            {alert.visible && <UncontrolledAlert className="Alert_Additions_Log" color={`${alert.color || "warning"}`} role="alert">
                {alert?.message || t("Welcome")}
            </UncontrolledAlert>}

            {categoryData && (
                <div className="page-content">
                    <Container fluid={true}>
                        <Breadcrumbs maintitle={t("Home")} title={t("Pain Area's")} titleLink="/category" breadcrumbItem={t("Pain Area Detail")} />
                        <Row>
                            <Col lg={4}>
                                <Card>
                                    <CardBody>
                                        <div className="text-center">
                                            <img src={categoryImage || categoryData?.image || NoImage} alt="Category" className="img-thumbnail" style={{ maxWidth: '100%', maxHeight: "300px" }} />
                                        </div>
                                        <div className="text-center mt-3">
                                            <input
                                                type="file"
                                                id="file"
                                                style={{ display: "none" }}
                                                onChange={handleImageChange}
                                            />
                                            <label htmlFor="file" className="btn btn-primary">
                                                {t("Change Image")}
                                            </label>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col lg={8}>
                                <Card>
                                    <CardBody>
                                        <CardTitle>{t("Edit Pain Area Detail")}</CardTitle>
                                        <CardSubtitle className="mb-3">
                                            {t("You can Edit Pain Area Detail from here.")}
                                        </CardSubtitle>
                                        <Form onSubmit={validation.handleSubmit}>
                                            <div className="mb-3">
                                                <Label className="form-label">{t("Pain Area Name")}</Label>
                                                <Input
                                                    name="name"
                                                    type="text"
                                                    onChange={validation.handleChange}
                                                    onBlur={validation.handleBlur}
                                                    value={validation.values.name || ""}
                                                    invalid={validation.touched.name && validation.errors.name}
                                                />
                                                {validation.touched.name && validation.errors.name && (
                                                    <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                                                )}
                                            </div>

                                            <div className="mb-3">
                                                <Label className="form-label">{t("Description")}</Label>
                                                <Input
                                                    type="textarea"
                                                    name="description"
                                                    rows="5"
                                                    onChange={validation.handleChange}
                                                    onBlur={validation.handleBlur}
                                                    value={validation.values.description || ""}
                                                    invalid={validation.touched.description && validation.errors.description}
                                                />
                                                {validation.touched.description && validation.errors.description && (
                                                    <FormFeedback type="invalid">{validation.errors.description}</FormFeedback>
                                                )}
                                            </div>

                                            <div className="d-flex flex-wrap gap-2">

                                            <Button type="submit" color="primary" disabled={isLoadingsub || isLoading}>
                                                    {isLoadingsub ? (
                                                        <Spinner size="sm" color="light" />
                                                    ) : (
                                                        `${t("Submit")}`
                                                    )}
                                                </Button>{" "}


                                                <Button onClick={() => { setIsAlertModal(true) }} color="danger" disabled={isLoadingsub || isLoading}>
                                                    {isLoading ? (<Spinner size="sm" color="light" />
                                                    ) : (
                                                        `${t("Delete")}`
                                                    )}
                                                </Button>{" "}

                                                {isAlertModal && <AlertModal selection={(e) => { e === true ? handleDelete() : setIsAlertModal(false) }} Display={null} />}

                                            </div>
                                        </Form>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </div>
            )}
        </React.Fragment>
    );
};

export default withTranslation()(FormValidations);
